/**
 * Every route, one file.
 *
 * Marketing, auth and graduation render outside the app shell. Everything else
 * renders inside it. Routes map to the segments the real product will use, so
 * the remap at handoff is mechanical rather than a redesign.
 *
 * Reserved and not linked from any nav: /p/:slug/logs (the build log already
 * proves the log component) and /deadline (an email artifact, delivered as
 * flat comps).
 */

import { Route, Routes } from 'react-router';

// The component-kit route is dev/design-review tooling, gone in a real
// build alongside the demo dock (App.tsx), kept whenever VITE_API_MODE=mock.
const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

import { AppShell } from './components/shell/AppShell';
import { AuthLayout, GraduationLayout, MarketingLayout } from './components/shell/BareLayout';

import Kit from './screens/Kit';
import NotFound from './screens/NotFound';

import Landing from './screens/marketing/Landing';
import Privacy from './screens/marketing/Privacy';
import Terms from './screens/marketing/Terms';

import Drop from './screens/drop/Drop';

import InviteClaim from './screens/auth/Claim';
import ProjectInvite from './screens/auth/ProjectInvite';
import OauthAuthorize from './screens/auth/OauthAuthorize';
import SignIn from './screens/auth/SignIn';
import SsoCallback from './screens/auth/SsoCallback';
import Waitlist from './screens/auth/Waitlist';

import GodView from './screens/admin/GodView';
import Platform from './screens/admin/Platform';
import Feedbacks from './screens/admin/Feedbacks';
import AdminWaitlist from './screens/admin/Waitlist';
import AdminBilling from './screens/admin/Billing';

import CodeHost from './screens/onboarding/CodeHost';
import ConnectCode from './screens/onboarding/ConnectCode';
import FirstBuild from './screens/onboarding/FirstBuild';
import NameApp from './screens/onboarding/NameApp';
import SetupComputer from './screens/onboarding/SetupComputer';

import ProjectsList from './screens/projects/ProjectsList';

import Activity from './screens/project/Activity';
import AddOns from './screens/project/AddOns';
import BuildFailure from './screens/project/BuildFailure';
import Database from './screens/project/Database';
import DeployDetail from './screens/project/DeployDetail';
import Deploys from './screens/project/Deploys';
import Files from './screens/project/Files';
import Keys from './screens/project/Keys';
import LogTail from './screens/project/LogTail';
import Overview from './screens/project/Overview';
import QueryRunner from './screens/project/QueryRunner';
import Settings from './screens/project/Settings';
import Bundle from './screens/project/Bundle';
import TableGrid from './screens/project/TableGrid';
import Traffic from './screens/project/Observability';
import Usage from './screens/project/Usage';

import Account from './screens/account/Account';

import Claim from './screens/graduation/Claim';
import DeadlineNotice from './screens/graduation/DeadlineNotice';
import Welcome from './screens/graduation/Welcome';

export function AppRoutes() {
  return (
    <Routes>
      {/* Outside the shell: no project scope, so no rail.

          Privacy is a marketing surface rather than an account one, because
          the person most likely to open it has no account yet and is deciding
          whether to give us an email address. It is linked from the footer and
          from the consent card, and both of those render on every layout in
          this group. */}
      <Route element={<MarketingLayout />}>
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
      </Route>

      <Route element={<AuthLayout />}>
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/sign-up" element={<SignIn />} />
        <Route path="/waitlist" element={<Waitlist />} />
        <Route path="/sso/callback" element={<SsoCallback />} />
        <Route path="/oauth/authorize" element={<OauthAuthorize />} />
        <Route path="/invite/:token" element={<InviteClaim />} />
      </Route>

      <Route element={<GraduationLayout />}>
        {/* The public upload link (F25). Outside the shell AND outside the
            auth layout: the person opening it has no account, is not being
            asked to make one, and must not be shown a sign-in surface. It sits
            on this layout for the same reason /claim does: a bare page with a
            footer and no consent card, because they followed a link somebody
            sent them rather than arriving cold. */}
        <Route path="/u/:token" element={<Drop />} />
        <Route path="/claim/:token" element={<Claim />} />
        <Route path="/welcome/:slug" element={<Welcome />} />
        <Route path="/deadline" element={<DeadlineNotice />} />
      </Route>

      {/* Inside the shell. */}
      <Route element={<AppShell />}>
        <Route path="/new" element={<NameApp />} />
        <Route path="/new/code" element={<CodeHost />} />
        <Route path="/new/connect" element={<ConnectCode />} />
        <Route path="/setup" element={<SetupComputer />} />
        <Route path="/new/building" element={<FirstBuild />} />

        <Route path="/projects" element={<ProjectsList />} />
        {/* Teams: inside the shell, because accepting needs a signed-in
            account whose email is the invited one. Never creates an account. */}
        <Route path="/project-invite/:token" element={<ProjectInvite />} />

        <Route path="/p/:slug" element={<Overview />} />
        <Route path="/p/:slug/deploys" element={<Deploys />} />
        <Route path="/p/:slug/deploys/:id" element={<DeployDetail />} />
        <Route path="/p/:slug/deploys/:id/failure" element={<BuildFailure />} />
        <Route path="/p/:slug/database" element={<Database />} />
        {USE_MOCK ? (
          <Route path="/p/:slug/database/query" element={<QueryRunner />} />
        ) : null}
        <Route path="/p/:slug/database/:table" element={<TableGrid />} />
        <Route path="/p/:slug/files" element={<Files />} />
        <Route path="/p/:slug/keys" element={<Keys />} />
        {/* Off the rail and off the command palette in every build (there is
            nothing to offer yet), but the screen itself still exists for
            design review against the fixture, same gate QueryRunner uses. */}
        {USE_MOCK ? <Route path="/p/:slug/add-ons" element={<AddOns />} /> : null}
        <Route path="/p/:slug/activity" element={<Activity />} />
        <Route path="/p/:slug/traffic" element={<Traffic />} />
        <Route path="/p/:slug/logs" element={<LogTail />} />
        <Route path="/p/:slug/usage" element={<Usage />} />
        <Route path="/p/:slug/settings" element={<Settings />} />
        {/* Deliberately not on the rail. It says "up to date" almost every
            time somebody could visit it, and a permanent rail slot is muscle
            memory spent on a page with nothing to do on it. It is reached
            from settings, and from a notice on the overview that appears
            exactly when there is something to act on. */}
        <Route path="/p/:slug/bundle" element={<Bundle />} />

        <Route path="/account" element={<Account />} />
        {/* The account roll-up of the same screen. It renders for somebody
            with no projects at all, which is the whole reason it exists as a
            destination rather than only as a tab inside one project. */}
        <Route path="/usage" element={<Usage />} />

        {/* F21. Both screens redirect a non-admin away - these routes are
            reachable by URL regardless of nav visibility, and the real gate
            is server-side (cloud-api's requireAdmin). */}
        <Route path="/admin/waitlist" element={<AdminWaitlist />} />
        <Route path="/admin/god-view" element={<GodView />} />
        <Route path="/admin/platform" element={<Platform />} />
        <Route path="/admin/feedbacks" element={<Feedbacks />} />
        <Route path="/admin/billing" element={<AdminBilling />} />

        {USE_MOCK ? <Route path="/kit" element={<Kit />} /> : null}

        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
